import {
  WORKOUT_BINDING_FIELD_LABELS,
  MAX_BINDINGS_BY_ZONE,
  MAX_BINDINGS_PER_ZONE,
  getMaxBindingsForZone,
} from './workoutFormConstants'
import {
  hasBindingRefMeta,
  hasSideViewFromBindingRow,
  maxRepeatCountForRow,
  normalizeMusicLoopForRow,
  parseRestSeconds,
  parseRepeatCount,
  pickActionAdaptationFields,
  readMusicLoopableFromBindingRow,
  readVideoDurationSeconds,
  readVideoLoopFromBindingRow,
  readVideoSideDurationFromBindingRow,
  recomputeActionBindingRow,
  resolveActionPlayUnitSeconds,
  round1,
  sumActionDurationSeconds,
} from './workoutActionBindingCalculator'

const ZONE_META = {
  actions: {
    label: '动作区',
    assetType: 'workout_action',
    emptyText: '未绑定动作',
  },
  music: {
    label: '音乐区',
    assetType: 'workout_music',
    emptyText: '未绑定音乐',
  },
  voices: {
    label: '语音区',
    assetType: 'workout_voice',
    emptyText: '未绑定语音',
  },
}

export function getBindingZoneLabel(zoneId) {
  return (ZONE_META[zoneId] && ZONE_META[zoneId].label) || zoneId
}

export function getBindingZoneEmptyText(zoneId) {
  return (ZONE_META[zoneId] && ZONE_META[zoneId].emptyText) || '未绑定'
}

/** 已选资源但说明书未挂上（refresh 失败 / 未完成）。 */
function bindingRowMissingRefMeta(row) {
  return Boolean(String((row && row.resource_id) || '').trim()) && !hasBindingRefMeta(row)
}

function validateActionRow(row, index, zoneLabel, totalRows) {
  const L = WORKOUT_BINDING_FIELD_LABELS
  const line = index + 1
  const isLast = index === totalRows - 1
  if (!String(row.resource_id || '').trim()) {
    return `${zoneLabel}第 ${line} 条须选择动作资源（resource_id）`
  }

  const metaDuration = readVideoDurationSeconds(row.ref_meta)
  const persistedDuration =
    row.video_duration_seconds != null && row.video_duration_seconds !== ''
      ? round1(row.video_duration_seconds)
      : null
  if (metaDuration != null && persistedDuration != null && metaDuration !== persistedDuration) {
    return `${zoneLabel}第 ${line} 条${L.videoDurationSeconds}与动作资源不一致（缓存 ${persistedDuration}s，资源 ${metaDuration}s），请刷新编排后保存`
  }

  if (totalRows > 1 && isLast && parseRestSeconds(row.rest_seconds, 0) > 0) {
    return `${zoneLabel}第 ${line} 条为最后动作，不能配置${L.restSeconds}`
  }

  const repeatMax = maxRepeatCountForRow(row)
  const requestedRepeat = parseRepeatCount(row.repeat_count, 0)
  if (requestedRepeat > repeatMax) {
    const loopLabel = readVideoLoopFromBindingRow(row) === false ? '不可循环' : '未知'
    return `${zoneLabel}第 ${line} 条动作视频${loopLabel}，${L.videoRepeatCount}不能超过 ${repeatMax}`
  }

  const recomputed = recomputeActionBindingRow(row, { index, totalRows })
  if (recomputed.video_duration_seconds == null) {
    return `${zoneLabel}第 ${line} 条${L.videoDurationSeconds}缺失，请确认动作资源 zip 已上传并成功保存`
  }
  if (hasSideViewFromBindingRow(row) && readVideoSideDurationFromBindingRow(row) == null) {
    return `${zoneLabel}第 ${line} 条为双视角动作，但侧视角时长缺失，请重新上传含 *_side_action.mp4 的 zip 或刷新编排`
  }
  if (resolveActionPlayUnitSeconds(row) == null) {
    return `${zoneLabel}第 ${line} 条无法计算播放总长，请确认主/侧视角时长完整`
  }
  if (recomputed.repeat_count < 1) {
    return `${zoneLabel}第 ${line} 条${L.videoRepeatCount}须至少为 1`
  }
  if (recomputed.duration_seconds <= 0) {
    return `${zoneLabel}第 ${line} 条${L.configuredPlaySeconds}须大于 0`
  }
  if (!isLast) {
    const rest = parseRestSeconds(recomputed.rest_seconds, 0)
    if (rest < 0) {
      return `${zoneLabel}第 ${line} 条${L.restSeconds}不能小于 0`
    }
  }
  return null
}

function validateMusicRow(row, index, zoneLabel) {
  const L = WORKOUT_BINDING_FIELD_LABELS
  const line = index + 1
  if (!String(row.resource_id || '').trim()) {
    return `${zoneLabel}第 ${line} 条须选择音乐资源（resource_id）`
  }
  const normalized = normalizeMusicLoopForRow(row)
  if (typeof normalized.loop !== 'boolean') {
    return `${zoneLabel}第 ${line} 条须选择${L.loop}`
  }
  if (readMusicLoopableFromBindingRow(row) === false && row.loop === true) {
    return `${zoneLabel}第 ${line} 条音乐资源不可循环，${L.loop} 只能为 false`
  }
  return null
}

function validateVoiceRow(row, index, zoneLabel) {
  const line = index + 1
  if (!String(row.resource_id || '').trim()) {
    return `${zoneLabel}第 ${line} 条须选择语音资源（resource_id）`
  }
  return null
}

const ROW_VALIDATORS = {
  actions: validateActionRow,
  music: validateMusicRow,
  voices: validateVoiceRow,
}

function validateTotalDurationSeconds(local) {
  const actions = Array.isArray(local.actions) ? local.actions : []
  if (!actions.length) {
    return null
  }
  // 与 buildExtaPayload 同一套 sanitize，再对已写入的 duration_seconds 求和——禁止对瘦身行重算
  const sanitized = sanitizeActionBindingsForSave(actions)
  const expected = sumActionDurationSeconds(sanitized)
  const actual = local.total_duration_seconds
  if (actual == null || actual === '') {
    return `缺少${WORKOUT_BINDING_FIELD_LABELS.totalDurationSeconds}`
  }
  const parsed = round1(actual)
  if (parsed !== expected) {
    return `${WORKOUT_BINDING_FIELD_LABELS.totalDurationSeconds}与动作区汇总不一致（应为 ${expected}s）`
  }
  return null
}

export function validateWorkoutCollectionBindings(local) {
  const data = local || {}
  for (const zoneId of Object.keys(ZONE_META)) {
    const rows = Array.isArray(data[zoneId]) ? data[zoneId] : []
    const { label } = ZONE_META[zoneId]
    if (rows.length > getMaxBindingsForZone(zoneId)) {
      return Promise.reject(`${label}最多绑定 ${getMaxBindingsForZone(zoneId)} 条`)
    }
    // 统一护栏：已选资源必须有说明书，禁止带着半份信息按单视角落库撞后端
    if (rows.some(bindingRowMissingRefMeta)) {
      return Promise.reject('合集子资源信息同步失败，请刷新页面后重试')
    }
    const validateRow = ROW_VALIDATORS[zoneId]
    for (let i = 0; i < rows.length; i += 1) {
      const rowError =
        zoneId === 'actions'
          ? validateRow(rows[i], i, label, rows.length)
          : validateRow(rows[i], i, label)
      if (rowError) {
        return Promise.reject(rowError)
      }
    }
  }
  const totalError = validateTotalDurationSeconds(data)
  if (totalError) {
    return Promise.reject(totalError)
  }
  return Promise.resolve()
}

export function sanitizeCollectionBindingRow(zoneId, row, rowIndex = 0, totalRows = 1) {
  const payload = {
    resource_id: row.resource_id,
  }
  if (zoneId === 'actions') {
    // 假定校验已保证有 ref_meta；此处只按说明书重算，不再保留无 meta 时的旧 duration
    const recomputed = recomputeActionBindingRow(row, { index: rowIndex, totalRows })
    return {
      ...payload,
      duration_seconds: recomputed.duration_seconds,
      repeat_count: recomputed.repeat_count,
      rest_seconds: parseRestSeconds(recomputed.rest_seconds, 0),
      ...pickActionAdaptationFields(recomputed),
    }
  }
  if (zoneId === 'music') {
    const normalized = normalizeMusicLoopForRow(row)
    return {
      ...payload,
      loop: normalized.loop,
    }
  }
  return payload
}

/**
 * 合集 actions 落库前统一归一化（含最后一条休息清零）。
 * 直接按行 sanitize：勿先 normalizeActionBindingsList——无 ref_meta 时那会按主视角压矮双视角 duration。
 * 保存路径须先过 {@link validateWorkoutCollectionBindings}（要求已有 ref_meta）。
 */
export function sanitizeActionBindingsForSave(actions) {
  const rows = Array.isArray(actions) ? actions : []
  return rows.map((row, index) => sanitizeCollectionBindingRow('actions', row, index, rows.length))
}

export { MAX_BINDINGS_BY_ZONE, MAX_BINDINGS_PER_ZONE, getMaxBindingsForZone, ZONE_META }
